import { useFormikContext } from 'formik'
import * as React from 'react'
import { ReactElement, useMemo } from 'react'
import Fieldset from '../Fieldset'
import LegacyAddressField from '../../Field/Address/LegacyAddressField'
import FormikPhoneNumberField from '../../Formik/FormikPhoneNumberField'
import PartyForm, { BasePartyDto, PartyProps, PartyStatusDropdown } from './PartyForm'
import { DefaultButton, PrimaryButton, Stack, StackItem } from '@fluentui/react'
import { ListItemDto } from '../../../config/partyConfig'
import KvkSearchDialog, { KvkSearchDialogProps } from '../../Dialog/ChamberOfCommerce/KvkSearchDialog'
import DialogButton from '../../../components/Button/DialogButton'
import useViewport, { Viewport } from '../../../hooks/useViewport'
import FormContainer from '../../../components/Form/FormContainer'
import { EntityType } from '../Accounting/EntityField'
import * as Yup from 'yup'
import useModules from '../../../hooks/module/useModules'
import { ModuleType } from '../../../context/GlobalContext'
import useAxios from '../../../hooks/useAxios'
import { useTranslation } from 'react-i18next'
import AttributesList from '../Attributes/AttributesList'
import {
    AttributeContextType,
    AttributeDefinitionDto,
    AttributeViewType,
    EntityAttributeType,
    getAttributeValidationSchema,
} from '../../../hooks/useAttributes'
import LegalStructureCombobox, { LegalStructureComboboxProps } from '../../Combobox/i18n/LegalStructureCombobox'
import FormikComboboxField from '../../Formik/FormikComboboxField'
import FormikCheckboxField from '../../Formik/FormikCheckboxField'
import FormikInputField from '../../Formik/FormikInputField'
import FormikTextareaField from '../../Formik/FormikTextareaField'
import { makeStyles, shorthands, tokens } from '@fluentui/react-components'
import MultiUnitDimensionFields from '../Accounting/MultiUnitDimensionFields'
import useRoles, { Role } from '../../../hooks/useRoles'
import { PartyTierDto } from '../../../config/finance/financeConfig'
import { PartyTierListItem } from '../Tier/PartyTierListItem'
import TierList from '../Tier/TierList'
import PartyTierFormContainer, { partyTierValidationSchema } from '../Tier/PartyTierFormContainer'
import { useSettings } from '../../../hooks/useSettings'
import { DateTime } from 'luxon'
import { formatMultipleEmailAddresses, yupEmailRegex } from '../../../helpers/regexHelper'

export const useStyles = makeStyles({
    formRow: {
        display: 'grid',
        ...shorthands.margin(tokens.spacingHorizontalM),
        marginLeft: tokens.spacingHorizontalXXL,
        ...shorthands.gap(tokens.spacingVerticalS),
    },
    oneQuarterThreeQuarter: {
        display: 'grid',
        gridTemplateColumns: '1fr 3fr',
    },
})

export type LegalPersonMutationDto = BasePartyDto & {
    name: string
    legalStructure: string
    poBoxAddressStreet?: string
    poBoxAddressNumber?: string
    poBoxAddressPostalCode?: string
    poBoxAddressLocality?: string
    poBoxAddressMunicipality?: string
    poBoxAddressCountry?: string
    financeTermInvoice: number
    financeTermPayment: number
    financeEmail: string
    vatNumber: string
    cocNumber: string
    fileReferenceRequired?: boolean
    reference?: string
}

const getValidationSchema = (translate: (key: string) => string, attributeDefinitions: AttributeDefinitionDto[]): Yup.ObjectSchema<any> => {
    return Yup.object().shape({
        name: Yup.string().label(translate('party.edit.company.name')).required(),
        email: Yup.string()
            .nullable()
            .label(translate('party.edit.contact.email'))
            .test({
                message: translate('base.form.email.warning'),
                test: values =>
                    !values ||
                    formatMultipleEmailAddresses(values)?.every(value => {
                        const result = value?.trim().match(yupEmailRegex)
                        return result?.length > 0
                    }),
            }),
        financeEmail: Yup.string()
            .nullable()
            .label(translate('party.edit.finance.financeemail'))
            .test({
                message: translate('base.form.email.warning'),
                test: values =>
                    !values ||
                    formatMultipleEmailAddresses(values)?.every(value => {
                        const result = value?.trim().match(yupEmailRegex)
                        return result?.length > 0
                    }),
            }),
        addressCountry: Yup.string()
            .nullable()
            .test({
                test: (value, context) =>
                    !!value?.trim() ||
                    !!value ===
                        !!(
                            context.parent.addressStreet?.trim() ||
                            context.parent.addressNumber?.trim() ||
                            context.parent.addressPostalCode?.trim() ||
                            context.parent.addressLocality?.trim()
                        ),
            })
            .label(translate('party.edit.address.country')),
        poBoxAddressCountry: Yup.string()
            .nullable()
            .label(translate('party.edit.address.country'))
            .test({
                test: (value, context) =>
                    !!value?.trim() ||
                    !!value ===
                        !!(
                            context.parent.poBoxAddressStreet?.trim() ||
                            context.parent.poBoxAddressNumber?.trim() ||
                            context.parent.poBoxAddressPostalCode?.trim() ||
                            context.parent.poBoxAddressLocality?.trim()
                        ),
            }),
        attributes: getAttributeValidationSchema(translate, attributeDefinitions),
    })
}

function Container(props: PartyProps<LegalPersonMutationDto>): ReactElement {
    const axios = useAxios()
    const modules = useModules()
    const roles = useRoles()
    const styles = useStyles()
    const { t: translate } = useTranslation()
    const { settings } = useSettings()

    const { values, setFieldValue, submitForm, isSubmitting, setValues, setErrors } = useFormikContext<LegalPersonMutationDto>()
    const { isViewport, getGap, viewport } = useViewport()
    const isHorizontal = useMemo<boolean>(() => isViewport('>=', Viewport.Large), [viewport])

    return (
        <FormContainer>
            <DialogButton<KvkSearchDialogProps>
                button={PrimaryButton}
                buttonProps={{ text: translate('party.edit.coc.search.title'), iconProps: { iconName: 'Search' } }}
                dialog={KvkSearchDialog}
                dialogProps={{
                    onSelectCompany: company => {
                        setValues({ ...values, ...company })
                        axios.get<ListItemDto>(`/party/search/${company.cocNumber}`).then(result => {
                            if (result?.data?.id) {
                                setErrors({ cocNumber: translate('party.edit.coc.cocNumber.error') })
                            }
                        })
                    },
                }}
            />
            <Fieldset legend={translate('party.edit.company.label')}>
                <FormikTextareaField<LegalPersonMutationDto>
                    required
                    labelKey="party.edit.company.name"
                    fieldName="name"
                    componentProps={{ rows: 2, autoFocus: true }}
                />
                <FormikInputField<LegalPersonMutationDto> labelKey="party.edit.company.shortname" fieldName="shortName" />
                <Stack horizontal={isHorizontal} tokens={{ childrenGap: getGap() }}>
                    <StackItem grow>
                        <FormikComboboxField<LegalPersonMutationDto, LegalStructureComboboxProps>
                            labelKey={'party.edit.company.legalstructure'}
                            fieldName={'legalStructure'}
                            component={LegalStructureCombobox}
                            componentProps={{
                                clearable: false,
                            }}
                        />
                    </StackItem>
                    <StackItem grow>
                        <FormikInputField<LegalPersonMutationDto> labelKey="party.edit.company.cocNumber" fieldName="cocNumber" />
                    </StackItem>
                </Stack>
            </Fieldset>

            <Fieldset legend={translate('party.edit.contact.label')} canToggle open>
                <FormikPhoneNumberField label={translate('party.edit.contact.phonelandline')} fieldName="phoneLandline" />
                <FormikInputField<LegalPersonMutationDto>
                    labelKey="party.edit.contact.email"
                    fieldName="email"
                    componentProps={{
                        onBlur: e => {
                            const newValue = formatMultipleEmailAddresses(e.target.value)
                            setFieldValue('email', newValue?.join(';'))
                        },
                    }}
                />
            </Fieldset>

            <LegacyAddressField legend={translate('party.edit.address.label')} prefix="address" open />
            <LegacyAddressField legend={translate('party.edit.poboxaddress.label')} prefix="poBoxAddress" />

            {modules.has(ModuleType.Attributes) && (props.attributes.activeAttributeDefinitions.length > 0 || values.attributes?.length > 0) && (
                <Fieldset legend={translate('party.edit.attributes.label')} canToggle>
                    <FormContainer>
                        <AttributesList
                            {...props}
                            attributeDefinitions={props.attributes.attributeDefinitions}
                            entityType={EntityAttributeType.Party}
                            context={[AttributeContextType.LegalPerson]}
                            viewType={AttributeViewType.Input}
                            orientation={isHorizontal ? 'horizontal' : 'vertical'}
                            entity={values}
                            entityId={values.id}
                            setFieldValue={setFieldValue}
                        />
                    </FormContainer>
                </Fieldset>
            )}

            {modules.has(ModuleType.Invoices) && (
                <Fieldset legend={translate('party.edit.finance.label')} canToggle>
                    <Stack horizontal tokens={{ childrenGap: getGap() }}>
                        <StackItem grow styles={{ root: { overflow: 'hidden' } }}>
                            <FormikInputField<LegalPersonMutationDto>
                                labelKey="party.edit.finance.financeterminvoice"
                                fieldName="financeTermInvoice"
                                componentProps={{
                                    contentAfter: translate('party.edit.finance.financetermpayment.days'),
                                }}
                            />
                        </StackItem>
                        <StackItem grow styles={{ root: { overflow: 'hidden' } }}>
                            <FormikInputField<LegalPersonMutationDto>
                                labelKey="party.edit.finance.financetermpayment"
                                fieldName="financeTermPayment"
                                componentProps={{
                                    contentAfter: translate('party.edit.finance.financetermpayment.days'),
                                }}
                            />
                        </StackItem>
                    </Stack>
                    <FormikInputField<LegalPersonMutationDto>
                        labelKey="party.edit.finance.financeemail"
                        fieldName="financeEmail"
                        componentProps={{
                            onBlur: e => {
                                const newValue = formatMultipleEmailAddresses(e.target.value)
                                setFieldValue('financeEmail', newValue?.join(';'))
                            },
                        }}
                    />
                    <FormikInputField<LegalPersonMutationDto> labelKey="party.edit.finance.vatnumber" fieldName="vatNumber" />
                    <div className={isHorizontal && styles.oneQuarterThreeQuarter}>
                        <FormikCheckboxField<LegalPersonMutationDto>
                            labelKey="party.edit.finance.filereferencerequired"
                            fieldName="fileReferenceRequired"
                        />
                        <FormikInputField<LegalPersonMutationDto> labelKey="party.edit.finance.reference" fieldName="reference" />
                    </div>
                </Fieldset>
            )}

            {modules.has(ModuleType.Accounting) && (
                <Fieldset legend={translate('party.edit.tier.label')} canToggle>
                    <TierList<LegalPersonMutationDto, PartyTierDto>
                        labelPrefixKey={'party.edit.tier'}
                        defaultTierValue={() => ({
                            type: 'PARTY_TIER',
                            validFrom: DateTime.now().startOf('month').toISODate(),
                            validTo: undefined,
                            salesRate: '0.00',
                            currency: settings.fileScaffold?.currency || 'EUR',
                            partyId: values.id,
                        })}
                        parentValues={values}
                        createListItem={(index: number, item: PartyTierDto, editTier) => (
                            <PartyTierListItem index={index} item={item} editTier={editTier} />
                        )}
                        createTierFormContainer={() => <PartyTierFormContainer />}
                        validationSchema={partyTierValidationSchema}
                    />
                </Fieldset>
            )}

            {modules.has(ModuleType.Accounting) && roles.any([Role.Invoice]) && (
                <MultiUnitDimensionFields
                    styles={styles}
                    fieldName="accounting"
                    selectedEntityTypes={[EntityType.CostCenter, EntityType.CostUnit, EntityType.GLA, EntityType.Project, EntityType.Customer]}
                />
            )}

            <Fieldset legend={translate('party.edit.other.label')} canToggle>
                <PartyStatusDropdown />
            </Fieldset>
            <Stack horizontal tokens={{ childrenGap: getGap() }}>
                <StackItem>
                    <DefaultButton primary={true} text={translate('action.save')} disabled={isSubmitting} onClick={submitForm} />
                </StackItem>
                <StackItem>
                    <DefaultButton primary={false} text={translate('action.cancel')} onClick={props.goBack} />
                </StackItem>
            </Stack>
        </FormContainer>
    )
}

export default function LegalPersonForm(props: PartyProps<LegalPersonMutationDto>): ReactElement {
    const { t: translate } = useTranslation()

    return (
        <PartyForm<LegalPersonMutationDto>
            {...props}
            formikProps={{
                ...props.formikProps,
                validationSchema: getValidationSchema(translate, props.attributes.activeAttributeDefinitions),
            }}
        >
            <Container {...props} />
        </PartyForm>
    )
}
